import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useParams, Navigate } from 'react-router-dom';
import { useAuthStore } from '../../stores/auth.store';
import { toast } from '../../stores/toast.store';
import { Icon } from '../../components/ui/Icon';
import { Breadcrumb } from '../../components/ui/Breadcrumb';
import { Chip } from '../../components/ui/Chip';
import { Avatar } from '../../components/ui/Avatar';
import { ActionsMenu, type ActionItem } from '../../components/ui/ActionsMenu';
import { DetailSkeleton } from '../../components/ui/Skeleton';
import { Mascotte } from '../../components/ui/Mascotte';
import { PageActions } from '../../components/layout/PageActions';
import { ModuleKey, PermissionLevel, hasAtLeast } from '@masamune/shared';
import { fmtDate } from '../../lib/utils';
import {
  taskDetailQuery, assignableUsersQuery, clientsLiteQuery,
  RECURRENCES, RECURRENCE_LABEL, PRIORITY_LABEL,
  dueInfo, toDateInput, toDateTimeInput, fromDateTimeInput, fmtDateTime, fullName, isDone, isHigh,
  googleCalendarUrl, downloadTaskIcs,
  type TaskRecurrenceKey,
} from './tasksApi';
import { useTaskActions } from './useTaskActions';
import { OpzioniPersone } from './OpzioniPersone';
import { TaskDetailMobile } from './TaskDetailMobile';
import { fraseLette, useAssistenteAttivo, useDividiInPiuTask, useRiscriviTask } from '../assistente/registroApi';
import { useIsMobile } from '../../hooks/useIsMobile';
import './tasks.css';
import './tasks-desktop.css';

/**
 * Dettaglio task — pagina compatta a una colonna, nello stile dei dettagli
 * della Contabilità (header editoriale + righe chiave-valore nude).
 * Solo l'essenziale: titolo, assegnato a, scadenza, promemoria, ricorrenza,
 * priorità, descrizione, cliente. Ogni campo salva al cambio (PATCH parziale).
 * Azioni: Completa/Riapri in evidenza; Google Calendar, .ics, Elimina nel menu.
 * Dal 28/10/2026, solo per gli Amministratori e ad Assistente acceso, nel menu anche
 * «Riscrivi titolo e descrizione» e «Dividi in più task» (os-planning/ai/fase-1-progetto.md,
 * decisioni 10 e 12); ad Assistente spento la pagina è identica a prima.
 */
export function TaskDetailPage() {
  const { id } = useParams<{ id: string }>();
  const isMobile = useIsMobile();
  if (!id) return <Navigate to="/tasks" replace />;
  // Sul telefono il dettaglio è TaskDetailMobile (righe con il valore a destra,
  // le scelte in un pannello dal basso). Sul computer resta questa, identica.
  return isMobile ? <div className="page"><TaskDetailMobile id={id} /></div> : <TaskDetailDesktop />;
}

function TaskDetailDesktop() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const { complete, reopen, update, remove } = useTaskActions();

  const hasWrite = useMemo(() => {
    if (!user) return false;
    const lvl = user.permissions.find((p) => p.module === ModuleKey.TASKS)?.level ?? PermissionLevel.NONE;
    return hasAtLeast(lvl, PermissionLevel.WRITE);
  }, [user]);

  const { data: task, isLoading, isError } = useQuery({ ...taskDetailQuery(id ?? ''), enabled: !!id, retry: false });
  // Per riga decide l'API (creatore, assegnatario o admin TASKS): canManage false = sola lettura
  const canWrite = hasWrite && (task?.canManage ?? true);
  const { data: users } = useQuery(assignableUsersQuery);
  const { data: clientsData } = useQuery(clientsLiteQuery);
  // L'Assistente (28/10/2026): tutti gli hook qui, sopra i return anticipati. La proposta
  // di Riscrivi sta qui perché la usano sia il menu ⋯ sia il blocco Descrizione; anche il
  // campo di modifica della descrizione sta qui, così «Riscrivi» non parte mentre la si
  // scrive a mano.
  const assistenteAttivo = useAssistenteAttivo();
  const riscrittura = useRiscriviTask(id);
  const divisione = useDividiInPiuTask();
  const [descrizioneAperta, setDescrizioneAperta] = useState(false);
  // Il dettaglio resta montato passando da una task all'altra: il campo aperto non la segue.
  useEffect(() => { setDescrizioneAperta(false); }, [id]);

  if (!id) return <Navigate to="/tasks" replace />;
  if (isError) return (
    <div className="page">
      <Breadcrumb items={[{ label: 'Task', to: '/tasks' }, { label: 'Dettaglio' }]} />
      <div className="max-w-[480px] mx-auto py-20 text-center">
        <h1 className="t-h1">Task non trovato</h1>
        <div className="mute mt-2">Forse è stato eliminato, o il link è sbagliato.</div>
      </div>
    </div>
  );
  if (isLoading || !task) return <DetailSkeleton header="chips" kpis={0} cards={1} />;

  const done = isDone(task.status);
  const due = dueInfo(task.dueDate, done);
  const gcal = googleCalendarUrl(task);
  const patch = (data: Record<string, unknown>) => update.mutate({ id: task.id, data });

  const onDelete = async () => {
    const r = await remove.mutateAsync({ id: task.id, title: task.title });
    if (r) navigate('/tasks');
  };
  const onIcs = () => {
    downloadTaskIcs(task.id, task.title).catch((e: any) => toast.alert('File non scaricato', e?.message ?? 'Riprova'));
  };

  // I tasti dell'Assistente (decisioni 10 e 12): Amministratori, Assistente che risponde,
  // task che chi guarda può gestire e mai privata (le task private non vanno al modello:
  // gli Amministratori hanno TASKS ADMIN, quindi canWrite da solo non basta).
  const conAssistente = assistenteAttivo && canWrite && !task.isPrivate;
  // Riscrivi no sui lavori previsti (28/10/2026): il titolo viene dal preventivo e va nel
  // resoconto al cliente. Dividi in più task sì, con la spunta Completa tolta.
  const puoRiscrivere = conAssistente && !task.inScope;

  const actionItems: ActionItem[] = [
    {
      key: 'riscrivi', label: 'Riscrivi titolo e descrizione', icon: 'pencil',
      // Con la proposta già a schermo, o la descrizione aperta a mano, la voce non serve.
      hidden: !puoRiscrivere || !!riscrittura.proposta || descrizioneAperta,
      disabled: riscrittura.inCorso,
      onClick: () => riscrittura.chiedi(),
    },
    {
      key: 'dividi', label: 'Dividi in più task', icon: 'list', hidden: !conAssistente, disabled: divisione.isPending,
      onClick: () => divisione.mutate({
        id: task.id, title: task.title, description: task.description, projectId: task.project?.id ?? null, clientId: task.client?.id ?? null,
        // Lavoro previsto e ricorrenza: la finestra li dice sotto «Completa la task di partenza» (28/10/2026).
        inScope: task.inScope, recurrence: task.recurrence,
      }),
    },
    { key: 'gcal', label: 'Aggiungi a Google Calendar', icon: 'calendarPlus', hidden: !gcal, onClick: () => window.open(gcal!, '_blank', 'noopener') },
    { key: 'ics', label: 'Scarica .ics', icon: 'export', hidden: !task.dueDate && !task.remindAt, onClick: onIcs },
    { key: 'delete', label: 'Elimina', icon: 'trash', danger: true, hidden: !canWrite, onClick: onDelete },
  ];

  return (
    // m-detail: sotto i 768px la scheda diventa una colonna sola e le coppie
    // etichetta/valore mettono l'etichetta sopra il valore. Sul computer la
    // classe non dichiara niente (os-planning/mobile.md §7).
    <div className="page m-detail">
      <Breadcrumb items={[{ label: 'Task', to: '/tasks' }, { label: task.title }]} />

      <div className="page-ed-hd flex items-end justify-between gap-4">
        <div className="min-w-0 max-w-[760px] flex-1">
          <TitleEditor title={task.title} canWrite={canWrite} done={done} onSave={(title) => patch({ title })} />
          <div className="detail-meta mt-2 truncate">
            {task.createdBy && <span><span className="mute-2 mr-1.5">Creato da</span><span className="v">{fullName(task.createdBy)}</span></span>}
            {task.createdAt && <><span className="mute-2">·</span><span className="mono">{fmtDate(task.createdAt)}</span></>}
            {done && task.completedAt && <><span className="mute-2">·</span><span><span className="mute-2 mr-1.5">Completato</span><span className="v mono">{fmtDateTime(task.completedAt)}</span></span></>}
          </div>
          {/* Alleggerito (26/10/2026): le pastiglie Da fare, scadenza, priorità e
              ricorrenza ripetevano le righe qui sotto. Resta solo l'avviso che
              serve davvero, quando il task è in ritardo o scade oggi. */}
          {!done && task.dueDate && due.tone !== 'mute' && (
            <div className={`tk-avviso${due.tone === 'alert' ? ' is-alert' : ''}`}>
              <Icon name="calendarClock" size={13} />
              {due.tone === 'alert' ? `In ritardo: doveva essere fatto il ${fmtDate(task.dueDate)}` : due.label === 'oggi' ? 'Scade oggi' : 'Scade domani'}
            </div>
          )}
        </div>

        {/* d-only sul bottone principale: sul telefono Completa/Riapri sta
            nella barra in fondo, dove arriva il pollice. Il menu ⋯ è roba
            secondaria: sul telefono PageActions lo porta nella barra in alto,
            accanto alla lente (mobile.md §4bis.6). Prima restava da solo su
            una riga sua sotto le pastiglie, 60px per tre puntini. Sul
            computer PageActions rende questo stesso div, identico. */}
        <PageActions className="flex gap-2 pb-1 shrink-0">
          {canWrite && (done ? (
            <button className="btn d-only" disabled={reopen.isPending} onClick={() => reopen.mutate({ id: task.id, title: task.title })}>
              <Icon name="undo" size={12} /> Riapri
            </button>
          ) : (
            <button className="btn btn-primary d-only" disabled={complete.isPending} onClick={() => complete.mutate({ id: task.id, title: task.title, prevStatus: task.status, dueDate: task.dueDate, recurrence: task.recurrence })}>
              <Icon name="check" size={12} /> Completa
            </button>
          ))}
          <ActionsMenu variant="ghost" label="Azioni" items={actionItems} disabled={remove.isPending} />
        </PageActions>
      </div>

      {/* Una colonna compatta: righe chiave-valore nude, salvataggio al cambio */}
      <div className="tk-detail">
        {update.isPending && <div className="tk-salvo">Salvo…</div>}
        <div className="ed-kv">
          <KvRow label="Chi lo fa">
            {canWrite ? (
              <select className="inp tk-inline" value={task.assignee?.id ?? ''} onChange={(e) => patch({ assigneeId: e.target.value || null })}>
                <OpzioniPersone users={users} meId={user?.id} />
              </select>
            ) : task.assignee ? <span className="row" style={{ gap: 6 }}><Avatar initials={task.assignee.initials} size={20} /> {fullName(task.assignee)}</span> : '—'}
          </KvRow>
          <KvRow label="Scadenza">
            {canWrite
              ? <input className="inp tk-inline mono" type="date" value={toDateInput(task.dueDate)} onChange={(e) => patch({ dueDate: e.target.value || null })} />
              : <span className={`mono ${due.tone === 'alert' ? 'text-alert' : ''}`}>{fmtDate(task.dueDate)}</span>}
          </KvRow>
          <KvRow label="Promemoria" hint="facoltativo, con l'ora">
            {canWrite
              ? <input className="inp tk-inline mono" type="datetime-local" value={toDateTimeInput(task.remindAt)} onChange={(e) => patch({ remindAt: fromDateTimeInput(e.target.value) })} />
              : <span className="mono">{fmtDateTime(task.remindAt)}</span>}
          </KvRow>
          <KvRow label="Si ripete" hint={task.recurrence && task.recurrence !== 'NONE' ? 'quando lo completi, nasce il prossimo' : undefined}>
            {canWrite ? (
              <select className="inp tk-inline" value={task.recurrence ?? 'NONE'} onChange={(e) => patch({ recurrence: e.target.value as TaskRecurrenceKey })}>
                {RECURRENCES.map((r) => <option key={r} value={r}>{RECURRENCE_LABEL[r]}</option>)}
              </select>
            ) : RECURRENCE_LABEL[task.recurrence ?? 'NONE']}
          </KvRow>
          <KvRow label="Priorità">
            {canWrite ? (
              <div className="seg-control tk-seg-sm">
                <button aria-pressed={!isHigh(task.priority)} onClick={() => { if (isHigh(task.priority)) patch({ priority: 'MEDIA' }); }}>Normale</button>
                <button aria-pressed={isHigh(task.priority)} onClick={() => { if (!isHigh(task.priority)) patch({ priority: 'ALTA' }); }}>Alta</button>
              </div>
            ) : PRIORITY_LABEL[task.priority]}
          </KvRow>
        </div>

        <div className="ed-divider" />

        <DescriptionBlock
          description={task.description}
          title={task.title}
          canWrite={canWrite}
          onSave={(description) => patch({ description })}
          open={descrizioneAperta}
          setOpen={setDescrizioneAperta}
          puoRiscrivere={puoRiscrivere}
          riscrittura={riscrittura}
        />

        <div className="ed-divider" />

        <div className="ed-kv">
          <KvRow label="Cliente" hint="facoltativo">
            {canWrite ? (
              <select className="inp tk-inline" value={task.client?.id ?? ''} onChange={(e) => patch({ clientId: e.target.value || null })}>
                <option value="">Nessuno</option>
                {(clientsData?.items ?? []).map((c) => <option key={c.id} value={c.id}>{c.shortName}</option>)}
              </select>
            ) : (task.client?.shortName ?? '—')}
          </KvRow>
          {task.project && (
            <KvRow label="Progetto">
              <a className="mono hover:underline cursor-pointer" onClick={() => navigate(`/projects/${task.project!.id}`)}>{task.project.code} · {task.project.name}</a>
            </KvRow>
          )}
        </div>
      </div>

      {/* Telefono: l'azione per cui si apre un task dal telefono, appoggiata
          sopra la barra di navigazione. m-only la spegne sul computer, dove la
          stessa azione sta già in cima. */}
      {canWrite && (
        <div className="m-actionbar m-only">
          {done ? (
            <button className="btn" disabled={reopen.isPending} onClick={() => reopen.mutate({ id: task.id, title: task.title })}>
              <Icon name="undo" size={12} /> Riapri
            </button>
          ) : (
            <button className="btn btn-primary" disabled={complete.isPending} onClick={() => complete.mutate({ id: task.id, title: task.title, prevStatus: task.status, dueDate: task.dueDate, recurrence: task.recurrence })}>
              <Icon name="check" size={12} /> Completa
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════
// Titolo modificabile: click sul titolo → input della stessa taglia
// ════════════════════════════════════════════════════════════════════
function TitleEditor({ title, canWrite, done, onSave }: { title: string; canWrite: boolean; done: boolean; onSave: (t: string) => void }) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(title);
  useEffect(() => { setValue(title); }, [title]);

  const commit = () => {
    setEditing(false);
    const v = value.trim();
    if (!v) { setValue(title); return; }
    if (v !== title) onSave(v);
  };

  if (editing) {
    return (
      <input
        className="ed-title tk-title-input"
        autoFocus
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') { e.preventDefault(); commit(); }
          else if (e.key === 'Escape') { setValue(title); setEditing(false); }
        }}
        aria-label="Titolo del task"
      />
    );
  }
  return (
    <h1
      className={`ed-title${canWrite ? ' tk-title-editable' : ''}${done ? ' line-through' : ''}`}
      title={canWrite ? 'Clicca per modificare' : undefined}
      onClick={() => { if (canWrite) setEditing(true); }}
    >
      {title}
    </h1>
  );
}

function KvRow({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <div className="ed-kv-row">
      <div className="k">{label}{hint && <div className="tk-kv-hint">{hint}</div>}</div>
      <div className="v">{children}</div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════
// Descrizione: blocco nudo con composer inline (come le note della fattura)
//
// Riscrivi titolo e descrizione (28/10/2026, fase-1-progetto.md decisione 12): il
// tasto sta accanto a «Modifica», in un flex gap-2 (.data-hd è space-between: da
// soli i tre pezzi si spargerebbero su tutta la riga). Mentre lavora, la mascotte
// piccola che pensa e «Riscrivo…». La proposta prende il posto del testo in due
// campi che non salvano da soli: «Usa questo testo» salva (anche quello corretto a
// mano), «Lascia com'è» non salva e non chiama niente. Sopra, le frasi di quello
// che l'Assistente ha tolto (una password, gli importi).
// ════════════════════════════════════════════════════════════════════
type Riscrittura = ReturnType<typeof useRiscriviTask>;

function DescriptionBlock({ description, title, canWrite, onSave, open, setOpen, puoRiscrivere, riscrittura }: {
  description: string | null;
  /** Il titolo di adesso: «Usa questo testo» lo confronta per dire nel toast cosa è cambiato. */
  title: string;
  canWrite: boolean;
  onSave: (d: string | null) => void;
  open: boolean;
  setOpen: (v: boolean) => void;
  /** L'Assistente può lavorare su questa task (Amministratore, acceso, task non privata). */
  puoRiscrivere: boolean;
  riscrittura: Riscrittura;
}) {
  const [text, setText] = useState('');
  const proposta = riscrittura.proposta;
  const lette = proposta ? fraseLette(proposta.lette, 'riscrivere') : null;
  return (
    <div>
      <div className="data-hd">
        <span className="tk-sez-t">Descrizione</span>
        {proposta ? (
          <span className="mute text-[12.5px]">Testo proposto da MUNE: correggilo se serve</span>
        ) : riscrittura.inCorso ? (
          <span className="flex items-center gap-2 mute text-[13px]">
            <Mascotte size={20} stato="pensa" title="MUNE sta riscrivendo" /> Riscrivo…
          </span>
        ) : canWrite && !open && (
          <div className="flex gap-2">
            {puoRiscrivere && (
              <button className="btn btn-sm btn-ghost" onClick={() => riscrittura.chiedi()}>
                <Icon name="pencil" size={12} /> Riscrivi titolo e descrizione
              </button>
            )}
            <button className="btn btn-sm btn-ghost" onClick={() => { setText(description ?? ''); setOpen(true); }}>
              <Icon name={description ? 'file' : 'plus'} size={12} /> {description ? 'Modifica' : 'Aggiungi'}
            </button>
          </div>
        )}
      </div>
      {proposta ? (
        <div>
          {proposta.tolto.map((frase) => (
            <div key={frase} className="flex items-start gap-2 text-[13px] mb-2">
              <Icon name="alert" size={13} className="mt-0.5 shrink-0" /> <span>{frase}</span>
            </div>
          ))}
          <label className="block mute text-[13px] font-medium mb-1.5" htmlFor="riscritto-titolo">Titolo</label>
          <input
            id="riscritto-titolo"
            className="inp"
            autoFocus
            maxLength={200}
            value={proposta.titolo}
            onChange={(e) => riscrittura.correggi({ titolo: e.target.value })}
          />
          <label className="block mute text-[13px] font-medium mb-1.5 mt-3" htmlFor="riscritto-descrizione">Descrizione</label>
          <textarea
            id="riscritto-descrizione"
            className="inp"
            rows={Math.min(14, Math.max(4, proposta.descrizione.split('\n').length + 1))}
            maxLength={5000}
            value={proposta.descrizione}
            onChange={(e) => riscrittura.correggi({ descrizione: e.target.value })}
          />
          {(lette || proposta.finta) && (
            <div className="mute text-[12.5px] mt-2">
              {[lette, proposta.finta ? 'Risposta di prova: MUNE è in prova e non ha chiamato Claude' : null].filter(Boolean).join('. ')}.
            </div>
          )}
          <div className="flex gap-2 mt-3">
            <button
              className="btn btn-sm btn-primary"
              disabled={!proposta.titolo.trim() || riscrittura.salvo}
              onClick={() => riscrittura.usa({ titolo: title, descrizione: description })}
            >
              <Icon name="check" size={12} /> {riscrittura.salvo ? 'Salvo…' : 'Usa questo testo'}
            </button>
            <button className="btn btn-sm btn-ghost" disabled={riscrittura.salvo} onClick={riscrittura.lascia}>Lascia com'è</button>
          </div>
        </div>
      ) : open ? (
        <>
          <textarea className="inp" rows={4} autoFocus placeholder="Di cosa si tratta, cosa serve per chiuderlo…" value={text} onChange={(e) => setText(e.target.value)} />
          <div className="flex gap-2 mt-2">
            <button className="btn btn-sm" onClick={() => { onSave(text.trim() || null); setOpen(false); }}>Salva</button>
            <button className="btn btn-sm btn-ghost" onClick={() => setOpen(false)}>Annulla</button>
          </div>
        </>
      ) : description ? (
        <div className="whitespace-pre-wrap leading-relaxed">{description}</div>
      ) : (
        <div className="mute text-[13px]">Nessuna descrizione.</div>
      )}
    </div>
  );
}
